import React from 'react';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import defaultImage from '../assets/img/Default.jpg';
import { capitalizeName, formatEdge, formatMoney, formatPct, imageUrl } from '../utils/format';
import { VALUE_EDGE } from '../utils/matches';

function fallbackToDefault(e) {
  if (e.currentTarget.src !== defaultImage) {
    e.currentTarget.src = defaultImage;
  }
}

export function Crest({ path, size = 'md', alt = '' }) {
  return <img className={`crest crest-${size}`} src={imageUrl(path)} alt={alt} loading="lazy" onError={fallbackToDefault} />;
}

export function TeamCell({ name, image, muted = false }) {
  return (
    <span className={`team-cell${muted ? ' muted' : ''}`}>
      <Crest path={image} />
      <span className="team-name">{capitalizeName(name)}</span>
    </span>
  );
}

export function LeagueCell({ league, image, compact = false }) {
  return (
    <span className="league-cell" title={capitalizeName(league)}>
      <Crest path={image} size="sm" />
      {!compact && <span>{capitalizeName(league)}</span>}
    </span>
  );
}

// Home / draw / away probabilities as one stacked bar. The model's pick is emphasised and, once
// the match is settled, the actual result is marked with a tick.
export function ProbabilityBar({ outcomes }) {
  const labelled = outcomes.map((o) => `${o.label} ${formatPct(o.prob)}`).join(', ');
  return (
    <div className="prob-bar" role="img" aria-label={`Win chances: ${labelled}`}>
      <div className="prob-track">
        {outcomes.map((o) => (
          <span key={o.key} className={`prob-seg prob-${o.key}${o.isPrediction ? ' is-pick' : ''}`}
            style={{ flexGrow: Math.max(o.prob || 0, 0.04) }} />
        ))}
      </div>
      <div className="prob-labels">
        {outcomes.map((o) => (
          <span key={o.key} className={`prob-label${o.isPrediction ? ' is-pick' : ''}${o.isResult ? ' is-result' : ''}`}>
            <span className="prob-key">{o.key === 'H' ? '1' : (o.key === 'D' ? 'X' : '2')}</span>
            {formatPct(o.prob, 0)}
            {o.isResult && <i className="pi pi-check" aria-label="actual result" />}
          </span>
        ))}
      </div>
    </div>
  );
}

// The model's bet with its stake; without a bet, the best blended edge.
export function BetBadge({ match }) {
  if (!match.pick) {
    return <EdgeBadge outcome={match.best} value={false} />;
  }
  const o = match.pick;
  return (
    <span className="edge-badge edge-value"
      title={`Model bet: ${o.label} at ${o.odds}, stake ${formatMoney(match.amount)}, blended edge ${formatEdge(o.edge)}`}>
      <i className="pi pi-star-fill" aria-hidden="true" />
      <span className="edge-pick">{o.key === 'D' ? 'Draw' : o.label}</span>
      <span className="edge-number">{formatMoney(match.amount)}</span>
      {o.edge !== null && <span className="edge-sub">{formatEdge(o.edge)}</span>}
    </span>
  );
}

export function EdgeBadge({ outcome, value }) {
  if (!outcome || outcome.edge === null) {
    return <span className="edge-badge edge-none">–</span>;
  }
  const level = value ? 'edge-value' : (outcome.edge > 0 ? 'edge-positive' : 'edge-negative');
  return (
    <span className={`edge-badge ${level}`}
      title={`${outcome.label} at ${outcome.odds}: ${formatEdge(outcome.edge)} expected return per unit`}>
      {value && <i className="pi pi-star-fill" aria-hidden="true" />}
      <span className="edge-pick">{outcome.key === 'D' ? 'Draw' : outcome.label}</span>
      <span className="edge-number">{formatEdge(outcome.edge)}</span>
    </span>
  );
}

export function ResultTag({ match }) {
  if (!match.settled) {
    return null;
  }
  const won = match.Prediction === match.ActualResult;
  return <span className={`result-tag ${won ? 'won' : 'lost'}`}>{won ? 'Model right' : 'Model wrong'}</span>;
}

export function StateMessage({ icon, title, detail, actionLabel, onAction }) {
  return (
    <div className="state-message" role="status">
      <i className={`pi ${icon}`} aria-hidden="true" />
      <h2>{title}</h2>
      {detail && <p>{detail}</p>}
      {actionLabel && <Button label={actionLabel} onClick={onAction} outlined size="small" />}
    </div>
  );
}

export function ListSkeleton({ rows = 6 }) {
  return (
    <div className="skeleton-list" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div className="skeleton-row" key={i}>
          <Skeleton width="4rem" height="1rem" />
          <Skeleton shape="circle" size="1.75rem" />
          <Skeleton width="30%" height="1rem" />
          <Skeleton width="25%" height="0.75rem" className="ml-auto" />
        </div>
      ))}
    </div>
  );
}

export const VALUE_HINT = `Model bet: blended edge of at least ${Math.round(VALUE_EDGE * 100)}% at odds of 3 or less; `
  + 'the stake grows with the edge (1 unit per 5%, up to 5)';

export const TARGET_HINT = `Lowest odds with a ${Math.round(VALUE_EDGE * 100)}% edge at this win chance`;
